import React, { useState, useRef, useMemo } from 'react';
import { IconUser, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface CustomUserMultiSelectProps {
  allUsers: User[];
  selectedUsers: User[];
  setSelectedUsers: (users: User[]) => void;
  onOpenSelector: () => void;
  disabled?: boolean;
}

const CustomUserMultiSelect: React.FC<CustomUserMultiSelectProps> = ({
  allUsers,
  selectedUsers,
  setSelectedUsers,
  onOpenSelector,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 过滤未选中的用户
  const filteredUsers = useMemo(() => {
    const kw = search.trim().toLowerCase();
    if (!kw) return [];
    return allUsers.filter(
      (u) =>
        !selectedUsers.some((su) => su.id === u.id) &&
        ((u.name && u.name.toLowerCase().includes(kw)) || (u.email && u.email.toLowerCase().includes(kw)))
    );
  }, [allUsers, search, selectedUsers]);

  // 选中用户（单选模式）
  const handleSelect = (user: User) => {
    // 始终用allUsers中完整对象
    const fullUser = allUsers.find((u) => u.id === user.id) || user;
    setSelectedUsers([...selectedUsers, fullUser]);
    setSearch('');
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  // 移除用户
  const handleRemove = (userId: string) => {
    setSelectedUsers(selectedUsers.filter((u) => u.id !== userId));
  };

  // 键盘操作
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || filteredUsers.length === 0) return;
    if (e.key === 'ArrowDown') {
      setHighlightIndex((i) => (i + 1) % filteredUsers.length);
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlightIndex((i) => (i - 1 + filteredUsers.length) % filteredUsers.length);
      e.preventDefault();
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      handleSelect(filteredUsers[highlightIndex]);
      setHighlightIndex(-1);
      e.preventDefault();
    }
  };

  // 下拉显示逻辑
  const handleInputFocus = () => {
    setShowDropdown(true);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setShowDropdown(true);
    setHighlightIndex(-1);
  };
  const handleInputBlur = () => {
    setTimeout(() => setShowDropdown(false), 150);
  };

  return (
    <div style={{ position: 'relative', minHeight: 40 }}>
      {/* 输入框 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          border: `2px solid ${disabled ? '#E5E7EB' : '#1A2233'}`,
          borderRadius: 10,
          background: disabled ? '#F9FAFB' : '#fff',
          padding: '0 10px',
          minHeight: 38,
          boxShadow: '0 1px 2px 0 rgba(16,30,54,0.03)',
          opacity: disabled ? 0.6 : 1,
        }}
        onClick={() => {
          if (disabled) return;
          inputRef.current?.focus();
        }}
      >
        <IconSearch size={16} color="#B0B8C4" style={{ marginRight: 4, flexShrink: 0 }} />
        <input
          ref={inputRef}
          value={search}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={selectedUsers.length === 0 ? t('adminConsole.customUserMultiSelect.searchPlaceholder') : ''}
          disabled={disabled}
          style={{
            border: 'none',
            outline: 'none',
            flex: 1,
            fontSize: 15,
            background: 'transparent',
            minWidth: 80,
            fontWeight: 500,
            color: disabled ? '#8B95A1' : '#222',
            height: 28,
            padding: 0,
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        <button
          type="button"
          style={{
            border: 'none',
            background: 'none',
            color: disabled ? '#D1D5DB' : '#B0B8C4',
            fontSize: 20,
            marginLeft: 6,
            cursor: disabled ? 'not-allowed' : 'pointer',
            padding: 0,
            width: 26,
            height: 26,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
            transition: 'background 0.2s',
            opacity: disabled ? 0.6 : 1,
          }}
          onClick={disabled ? undefined : onOpenSelector}
          tabIndex={-1}
          disabled={disabled}
        >
          ＋
        </button>
      </div>
      {/* 标签区：单独一排 */}
      {selectedUsers.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: 6,
            marginBottom: 2,
            maxHeight: 100,
            overflowY: 'auto',
            paddingRight: 4,
          }}
        >
          {selectedUsers.map((user) => (
            <span
              key={user.id}
              className="flex items-center bg-gray-100 rounded-full px-2 py-0.5 text-xs text-gray-800 max-w-xs group"
              style={{ border: 'none', fontWeight: 500, flexShrink: 0 }}
            >
              <IconUser size={13} style={{ marginRight: 3, color: '#B0B8C4' }} />
              <span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.name || user.email}
              </span>
              <button
                className="ml-2 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-base"
                onClick={(e) => {
                  if (disabled) return;
                  e.stopPropagation();
                  handleRemove(user.id);
                }}
                title={t('adminConsole.customUserMultiSelect.remove')}
                type="button"
                style={{
                  marginLeft: 3,
                  fontSize: 14,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.3 : 1,
                }}
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {/* 下拉列表：单选 */}
      {showDropdown && filteredUsers.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 38,
            left: 0,
            right: 0,
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 4px 16px 0 rgba(30,41,59,0.10)',
            zIndex: 99,
            padding: '6px 0',
            maxHeight: 200,
            overflowY: 'auto',
            border: '1.5px solid #E5E7EB',
          }}
        >
          <div style={{ padding: '2px 12px 4px 12px', fontSize: 12, color: '#8B95A1', fontWeight: 600 }}>
            {t('adminConsole.customUserMultiSelect.people')}
          </div>
          {filteredUsers.map((user, idx) => (
            <div
              key={user.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 12px',
                cursor: 'pointer',
                background: highlightIndex === idx ? '#F4F6FA' : '#fff',
                transition: 'background 0.2s',
                borderLeft: highlightIndex === idx ? '2px solid #1A2233' : '2px solid transparent',
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(user);
              }}
              onMouseEnter={() => setHighlightIndex(idx)}
              onMouseLeave={() => setHighlightIndex(-1)}
            >
              <IconUser size={15} color="#B0B8C4" style={{ marginRight: 7 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 13,
                    color: '#222',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.name || user.email}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: '#8B95A1',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.email}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomUserMultiSelect;
